import { SubmittalStage } from '@/contexts/CommentTypeContext';
import { FilePage } from '@/types/FilePages';
import { MutableComment, Product, SpecDictionary, Submittal, SubmittalMessage, User, UsageData } from '@/types/types'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export interface GetProductsAndCommentsParams {
    submittalId: number;
    submittalStage: SubmittalStage;
}

export interface GenerateCommentsParams {
    submittalId: number;
    productId?: number;
};

export interface GetFilePagesParams {
    fileId: number;
    fileType: string;
};

interface EnhanceFilePageParams {
    filePageId: number;
    fileType: string;
};

interface UpdateProductArgs {
    product: Partial<Product>;
    force?: boolean;
}

interface DeleteProductArgs {
    productId: number;
    force?: boolean;
}

interface DuplicateCommentArgs {
    commentId: number;
    page: number;
}

interface DuplicateProductCommentsArgs {
    sourceProductId: number;
    destinationProductIds: number[];
}

interface DuplicateCommentsArgs {
    commentIds: number[];
    productIds: number[];
}

interface GetCompanySubmittalsParams {
    companyId: number;
    since: string;
}

interface GetCompanyUsageParams {
    companyId: number;
    month: string;
}

export const api = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: 'https://api.getspect.ai/v3',
        credentials: 'include'
    }),
    tagTypes: ['Submittal', 'Product', 'Comment', 'FilePage', 'Users', 'PendingSubmittals', 'CompanyUsage', 'CompanySubmittals', 'Project'],
    endpoints: (builder) => ({
        getUser: builder.query<User, number>({
            query: (userId) => ({
                url: 'https://api.getspect.ai/v3/user',
                params: { userId }
            })
        }),
        getProductsAndComments: builder.query<SubmittalMessage, GetProductsAndCommentsParams>({
            query: ({ submittalId, submittalStage }) => ({
                url: `/submittal/${submittalId}`,
                params: { submittalStage }
            }),
            providesTags: (result, _err, _arg) =>
                result
                    ? [...result.products.map(({ id }) => ({ type: 'Product' as const, id })), 'Product',
                    ...result.comments.map(({ id }) => ({ type: 'Comment' as const, id })), 'Comment',
                    { type: 'Submittal' as const, id: result.submittal.id }, 'Submittal']
                    : ['Product', 'Comment', 'Submittal'],

        }),
        addComments: builder.mutation<void, MutableComment[]>({
            query: (comment) => ({
                url: `/comment`,
                method: 'POST',
                body: comment
            }),
            invalidatesTags: (_result, _error, _arg) => ['Comment'],
        }),
        duplicateComment: builder.mutation<void, DuplicateCommentArgs>({
            query: (args) => ({
                url: `/comment/${args.commentId}/duplicate`,
                method: 'POST',
                params: {
                    page: args.page
                }
            }),
            invalidatesTags: (_result, _error, _arg) => ['Comment'],
        }),
        duplicateProductComments: builder.mutation<void, DuplicateProductCommentsArgs>({
            query: (args) => ({
                url: `/product/${args.sourceProductId}/duplicate-comments`,
                method: 'POST',
                body: args.destinationProductIds
            }),
            invalidatesTags: (_result, _error, _arg) => ['Comment'],
        }),

        duplicateComments: builder.mutation<void, DuplicateCommentsArgs>({
            query: (args) => ({
                url: `/comment/duplicate`,
                method: 'POST',
                body: args
            }),
            invalidatesTags: (_result, _error, _arg) => ['Comment'],
        }),
        updateComment: builder.mutation<void, MutableComment>({
            query: (comment) => ({
                url: `/comment`,
                method: 'PUT',
                body: comment
            }),
            onQueryStarted({ id, submittalId, ...patch }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData('getProductsAndComments', { submittalId, submittalStage: 'CURRENT' }, (draft) => {
                        const draftComment = draft.comments.find(c => c.id == id)
                        if (draftComment) {
                            Object.assign(draftComment, patch)
                        }
                    })
                )
                queryFulfilled.catch(patchResult.undo)
            },
            invalidatesTags: (_result, _error, arg) => [{ type: 'Comment', id: arg.id }],
        }),
        deleteComments: builder.mutation<void, number[]>({
            query: (commentIds) => ({
                url: `/comment`,
                method: 'DELETE',
                params: { commentIds }
            }),
            invalidatesTags: (_result, _error, arg) => ['Comment'],
        }),
        generateComments: builder.mutation<string, GenerateCommentsParams>({
            query: (body) => ({
                url: `/comment/generate`,
                method: 'POST',
                body,
                responseHandler: async (response) => response.text()
            }),
            invalidatesTags: (_result, _error, arg) => ['Comment', { type: 'Submittal', id:arg.submittalId}],
        }),
        generateSelectedSpecAreas: builder.mutation<void, number[]>({
            query: (productIds) => ({
                url: `/product/generate-selected-spec-areas`,
                method: 'POST',
                body: productIds
            })
        }),
        addProduct: builder.mutation<number, Partial<Product>>({
            query: (product) => ({
                url: `/product`,
                method: 'POST',
                body: product
            }),
            invalidatesTags: (_result, _error, _arg) => ['Product'],
        }),
        updateProduct: builder.mutation<void, UpdateProductArgs>({
            query: (params) => ({
                url: `/product`,
                method: 'PUT',
                body: params.product,
                params: { force: params.force }
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Product', id: arg.product.id }],
        }),
        deleteProduct: builder.mutation<void, DeleteProductArgs>({
            query: (args) => ({
                url: `/product`,
                method: 'DELETE',
                params: {
                    productId: args.productId,
                    force: args.force
                }
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Product', id: arg.productId }],
        }),
        releaseToContractor: builder.mutation<void, number>({
            query: (submittalId) => ({
                url: `/submittal/${submittalId}/release-to-contractor`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Submittal', id: arg}],
        }),
        returnFromContractor: builder.mutation<void, number>({
            query: (submittalId) => ({
                url: `/submittal/${submittalId}/return-from-contractor`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Submittal', id: arg}],
        }),
        releaseToCustomer: builder.mutation<void, number>({
            query: (submittalId) => ({
                url: `/submittal/${submittalId}/release-to-customer`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Submittal', id: arg}],
        }),
        updateSubmittal: builder.mutation<void, Partial<Submittal>>({
            query: (submittal) => ({
                url: `/submittal`,
                method: 'PUT',
                body: submittal
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Submittal', id: arg.id }],
        }),
        getSpecDicts: builder.query<SpecDictionary[], number>({
            query: (submittalId) => ({
                url: `/submittal/${submittalId}/dictionary`
            })
        }),
        getFilePages: builder.query<FilePage[], GetFilePagesParams>({
            query: ({ fileId, fileType }) => ({
                url: `/${fileType}/${fileId}/pdf`
            }),
            providesTags: (result, _error, _arg) =>
                result
                  ? [...result.map(({ id }) => ({ type: 'FilePage' as const, id })), 'FilePage']
                  : ['FilePage'],
        }),
        enhanceFilePage: builder.mutation<FilePage, EnhanceFilePageParams>({
            query: ({ filePageId, fileType }) => ({
                url: `/pdf/${fileType}-page/${filePageId}/enhance`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'FilePage', id: arg.filePageId }],
        }),
        getAllUsers: builder.query<User[], void>({
            query: () => ({
              url: '/user/all',
              method: 'GET',
            }),
            providesTags: ['Users'],
        }),
        getCompanySubmittals: builder.query<Submittal[], GetCompanySubmittalsParams>({
            query: ({ companyId, since }) => ({
                url: `/company/${companyId}/submittals`,
                params: { since }
            }),
            providesTags: ['CompanySubmittals']
        }),
        getPendingSubmittals: builder.query<Submittal[], number>({
            query: (companyId) => ({
                url: `/company/${companyId}/pending-submittals`
            }),
            providesTags: ['PendingSubmittals']
        }),
        getCompanyUsage: builder.query<UsageData, GetCompanyUsageParams>({
            query: ({ companyId, month }) => ({
                url: `/company/${companyId}/usage`,
                params: { month }
            }),
            providesTags: ['CompanyUsage']
        }),
        toggleProjectFavorite: builder.mutation<void, number>({
            query: (projectId) => ({
                url: `/project/${projectId}/favorite`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, _arg) => ['Project'],
        }),
    }),
})

export const { useLazyGetUserQuery, 
    useGetProductsAndCommentsQuery, useUpdateCommentMutation,
    useAddCommentsMutation, useDuplicateCommentMutation,
    useDuplicateProductCommentsMutation,
    useDuplicateCommentsMutation,
    useDeleteCommentsMutation, useGenerateCommentsMutation,
    useGenerateSelectedSpecAreasMutation, useAddProductMutation,
    useUpdateProductMutation, useDeleteProductMutation,
    useReleaseToContractorMutation,
    useReturnFromContractorMutation, useReleaseToCustomerMutation,
    useUpdateSubmittalMutation, useGetSpecDictsQuery, useGetFilePagesQuery,
    useEnhanceFilePageMutation, useGetAllUsersQuery,
    useGetPendingSubmittalsQuery,
    useGetCompanyUsageQuery,
    useToggleProjectFavoriteMutation,
    useLazyGetCompanyUsageQuery } = api;
